/**
 * Helpers for the clinician Wisp / Typeform attachment download flow (TH-3241).
 *
 * Typeform locks insurance file URLs (https://api.typeform.com/responses/files/...).
 * The frontend must never open those URLs directly or embed them in <img>/<a>; instead
 * it POSTs the URL to the TBD backend, which downloads with the Wisp PAT and returns the
 * raw file bytes. These helpers keep the validation and parsing logic testable and shared.
 */

// Backend rejects URLs longer than this; mirror the limit client-side for a fast, clear error.
export const TYPEFORM_URL_MAX_LENGTH = 2048;

/**
 * Validate that a value is an https Typeform URL (typeform.com or *.typeform.com).
 * Mirrors the backend contract so we fail fast before making a request.
 * @param {string} value
 * @returns {{ valid: boolean, error: string|null }}
 */
export const validateTypeformUrl = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) {
    return { valid: false, error: "Enter a Typeform file URL to download." };
  }
  if (trimmed.length > TYPEFORM_URL_MAX_LENGTH) {
    return {
      valid: false,
      error: `The URL is too long (max ${TYPEFORM_URL_MAX_LENGTH} characters).`,
    };
  }

  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch (err) {
    return { valid: false, error: "Enter a valid URL (including https://)." };
  }

  if (parsed.protocol !== "https:") {
    return { valid: false, error: "The URL must start with https://." };
  }

  if (!isTypeformHost(parsed.hostname)) {
    return {
      valid: false,
      error: "Only Typeform file URLs (typeform.com) are allowed.",
    };
  }

  return { valid: true, error: null };
};

/**
 * True when hostname is exactly typeform.com or a subdomain such as api.typeform.com.
 * @param {string} hostname
 * @returns {boolean}
 */
export const isTypeformHost = (hostname) => {
  const host = (hostname || "").toLowerCase();
  return host === "typeform.com" || host.endsWith(".typeform.com");
};

/**
 * Map a backend error status to a clinician-friendly message. The download endpoint
 * returns empty error bodies, so the HTTP status is the only signal we have.
 * @param {number|string|undefined} status
 * @returns {string}
 */
export const messageForDownloadStatus = (status) => {
  switch (Number(status)) {
    case 400:
      return "That doesn't look like a valid Typeform file URL. Check the link and try again.";
    case 401:
      return "Your session has expired. Please sign in again and retry.";
    case 403:
      return "You don't have permission to download this attachment.";
    case 404:
      return "The attachment could not be downloaded. The Typeform link may have expired.";
    default:
      return "The attachment could not be downloaded. Please try again, and report it if the problem persists.";
  }
};

/**
 * Extract a filename from a Content-Disposition header, supporting both the plain
 * `filename="..."` form and the RFC 5987 `filename*=UTF-8''...` form.
 * @param {string} [header]
 * @returns {string|null}
 */
export const filenameFromContentDisposition = (header) => {
  if (!header || typeof header !== "string") return null;

  // RFC 5987 extended form takes precedence when present.
  const extendedMatch = header.match(/filename\*\s*=\s*([^;]+)/i);
  if (extendedMatch) {
    const raw = extendedMatch[1].trim().replace(/^UTF-8''/i, "");
    try {
      const decoded = decodeURIComponent(raw);
      if (decoded) return sanitizeFilename(decoded);
    } catch (err) {
      // Fall through to the plain filename form.
    }
  }

  const quotedMatch = header.match(/filename\s*=\s*"([^"]*)"/i);
  if (quotedMatch && quotedMatch[1]) {
    return sanitizeFilename(quotedMatch[1]);
  }

  const bareMatch = header.match(/filename\s*=\s*([^;]+)/i);
  if (bareMatch && bareMatch[1]) {
    return sanitizeFilename(bareMatch[1].trim());
  }

  return null;
};

/**
 * Best-effort filename derived from the URL path, used when the server does not send
 * a Content-Disposition header.
 * @param {string} url
 * @returns {string|null}
 */
export const filenameFromUrl = (url) => {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];
    if (!last) return null;
    return sanitizeFilename(decodeURIComponent(last));
  } catch (err) {
    return null;
  }
};

/**
 * Resolve the best filename for a downloaded attachment.
 * @param {string} [contentDisposition]
 * @param {string} [sourceUrl]
 * @returns {string}
 */
export const resolveDownloadFilename = (contentDisposition, sourceUrl) => {
  return (
    filenameFromContentDisposition(contentDisposition) ||
    filenameFromUrl(sourceUrl) ||
    "typeform-attachment"
  );
};

/**
 * Whether a MIME type can be previewed inline in the browser.
 * @param {string} [contentType]
 * @returns {"image"|"pdf"|null}
 */
export const previewKindForContentType = (contentType) => {
  const type = (contentType || "").toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("application/pdf")) return "pdf";
  return null;
};

// Strip path separators and control characters so a malicious filename cannot escape
// the intended save location or break the download attribute.
const sanitizeFilename = (name) => {
  return String(name)
    .replace(/[\\/]+/g, "_")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .trim();
};
